import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { allProducts } from '@/mocks/productsData';

interface RelatedProductsProps {
  currentProductId: number;
  category: string;
  currentPrice: number;
}

function SectionReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={`scroll-reveal ${isVisible ? 'scroll-reveal-visible' : ''} ${delay > 0 ? `scroll-reveal-delay-${Math.min(delay, 6)}` : ''} ${className}`}
    >
      {children}
    </div>
  );
}

export default function RelatedProducts({ currentProductId, category, currentPrice }: RelatedProductsProps) {
  const navigate = useNavigate();

  const related = allProducts
    .filter((p) => p.id !== currentProductId)
    .sort((a, b) => {
      const aCatMatch = a.category === category ? 0 : 1;
      const bCatMatch = b.category === category ? 0 : 1;
      if (aCatMatch !== bCatMatch) return aCatMatch - bCatMatch;
      return Math.abs(a.price - currentPrice) - Math.abs(b.price - currentPrice);
    })
    .slice(0, 3);

  if (related.length === 0) return null;

  return (
    <SectionReveal>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#FAF8F5] via-[#F5F1EB] to-[#FAF8F5] border border-[#E8E0D6] p-6 md:p-10">
        {/* 背景裝飾光暈 */}
        <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[#D4A574]/8 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-[#1B3D2A]/6 blur-2xl pointer-events-none" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#1B3D2A]/10 flex items-center justify-center">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-heart-3-line text-[#1B3D2A]" />
              </div>
            </div>
            <span className="text-xs font-semibold text-[#D4A574] tracking-wider uppercase">You May Also Like</span>
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-[#1B3D2A] mb-6 md:mb-8">
            您可能也會喜歡
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
            {related.map((product, i) => {
              const isHighPrice = product.price >= 45000;
              return (
                <div
                  key={product.id}
                  className={`group bg-white rounded-2xl border border-[#E8E0D6] overflow-hidden cursor-pointer hover:border-[#D4A574]/40 hover:shadow-lg hover:shadow-[#1B3D2A]/5 transition-all duration-500`}
                  style={{ animation: `fade-in-up 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${0.1 + i * 0.12}s both` }}
                  onClick={() => navigate(`/product/${product.id}`)}
                >
                  {/* 圖片區域 */}
                  <div className="relative w-full h-52 md:h-64 overflow-hidden bg-gradient-to-br from-[#FAF8F5] to-[#F0EAE0]">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                    />

                    {/* 底部漸變疊層 */}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#1B3D2A]/40 via-[#1B3D2A]/10 to-transparent" />

                    {/* 頂部標籤 */}
                    {isHighPrice && (
                      <div className="absolute top-3 left-3">
                        <span className="inline-block px-3 py-1 rounded-full bg-[#1B3D2A]/85 backdrop-blur-sm text-white text-xs font-bold tracking-wide shadow-sm">
                          熱銷推薦
                        </span>
                      </div>
                    )}

                    {/* Hover 浮現標籤列 */}
                    <div className="absolute inset-x-0 bottom-3 flex flex-wrap justify-center gap-1.5 px-3 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                      {product.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 rounded-full bg-white/90 backdrop-blur-sm text-[#1B3D2A] text-xs font-medium shadow-sm"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {/* Hover 查看按鈕 */}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-400">
                      <div className="w-12 h-12 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-lg">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className="ri-eye-line text-[#1B3D2A] text-lg" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* 文字資訊區 */}
                  <div className="p-4 md:p-5">
                    <div className="text-xs font-semibold text-[#D4A574] uppercase tracking-wider mb-1.5">
                      {product.subtitle}
                    </div>
                    <h3 className="text-base font-bold text-[#1B3D2A] leading-snug group-hover:text-[#D4A574] transition-colors duration-300">
                      {product.name}
                    </h3>

                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-xs text-[#9A8A7A] font-medium">NT$</span>
                        <span className="text-lg font-bold text-[#1B3D2A]">
                          {product.price.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#1B3D2A] group-hover:text-[#D4A574] transition-colors duration-300">
                        <span className="hidden group-hover:inline whitespace-nowrap">查看詳情</span>
                        <div className="w-7 h-7 rounded-full bg-[#1B3D2A]/8 group-hover:bg-[#1B3D2A] flex items-center justify-center transition-colors duration-300">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-arrow-right-line text-[#1B3D2A] text-xs group-hover:text-white transition-colors duration-300" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </SectionReveal>
  );
}
// ... existing code ...